import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, categoriesAPI, suppliersAPI } from '../lib/api';
import { BarcodeScanner } from '../components/scanner/BarcodeScanner';
import { ImageCapture } from '../components/scanner/ImageCapture';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '../components/ui/sheet';
import { toast } from 'sonner';
import { 
    Scan, 
    Camera, 
    Package, 
    ArrowLeft, 
    Plus, 
    Check, 
    AlertCircle,
    Edit,
    Loader2,
    Keyboard
} from 'lucide-react';

export default function ScanProduct() {
    const navigate = useNavigate();
    const [scannerOpen, setScannerOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [manualEntry, setManualEntry] = useState(false);
    const [existingProduct, setExistingProduct] = useState(null);
    const [recentScans, setRecentScans] = useState([]);
    const barcodeInputRef = useRef(null);

    const [form, setForm] = useState({
        name: '',
        sku: '',
        barcode: '',
        description: '',
        category_id: '',
        price: '',
        cost_price: '',
        stock_quantity: '1',
        low_stock_threshold: '10',
        image_url: '',
        auto_reorder: false,
        reorder_supplier_id: '',
        reorder_quantity: '20'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [categoriesRes, suppliersRes] = await Promise.all([
                categoriesAPI.getAll(),
                suppliersAPI.getAll()
            ]);
            setCategories(categoriesRes.data);
            setSuppliers(suppliersRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm({
            name: '',
            sku: '',
            barcode: '',
            description: '',
            category_id: '',
            price: '',
            cost_price: '',
            stock_quantity: '1',
            low_stock_threshold: '10',
            image_url: '',
            auto_reorder: false,
            reorder_supplier_id: '',
            reorder_quantity: '20'
        });
        setExistingProduct(null);
    };

    const handleScan = async (barcode) => {
        setScannerOpen(false);
        
        // Vibrate feedback
        if (navigator.vibrate) {
            navigator.vibrate(100);
        }

        // Check if product exists by SKU/barcode
        try {
            let found = null;
            try {
                const skuRes = await productsAPI.getBySku(barcode);
                found = skuRes.data;
            } catch {
                const response = await productsAPI.getAll({ search: barcode });
                found = response.data.find(p => p.barcode === barcode || p.sku === barcode) || null;
            }

            if (found) {
                // Existing product - pre-fill form
                setExistingProduct(found);
                setForm({
                    name: found.name,
                    sku: found.sku,
                    barcode: found.barcode || barcode,
                    description: found.description || '',
                    category_id: found.category_id,
                    price: found.price.toString(),
                    cost_price: found.cost_price.toString(),
                    stock_quantity: '1', // Default to adding 1
                    low_stock_threshold: found.low_stock_threshold.toString(),
                    image_url: found.image_url || '',
                    auto_reorder: found.auto_reorder || false,
                    reorder_supplier_id: found.reorder_supplier_id || '',
                    reorder_quantity: (found.reorder_quantity || 20).toString()
                });
                toast.success(`Found: ${found.name}`);
            } else {
                // New product
                setExistingProduct(null);
                setForm(prev => ({
                    ...prev,
                    barcode: barcode,
                    sku: barcode // Use barcode as SKU by default
                }));
                toast.info('New product - please fill in details');
            }

            // Add to recent scans
            setRecentScans(prev => [{
                barcode,
                timestamp: new Date(),
                found: !!found,
                name: found?.name
            }, ...prev.slice(0, 9)]);

            setSheetOpen(true);
        } catch (error) {
            toast.error('Error looking up product');
        }
    };

    const handleManualBarcode = (e) => {
        if (e.key === 'Enter' && form.barcode) {
            handleScan(form.barcode);
        }
    };

    const handleImageCapture = (imageData) => {
        setCameraOpen(false);
        setForm(prev => ({ ...prev, image_url: imageData }));
        toast.success('Photo captured!');
    };

    const handleSave = async () => {
        if (!form.name || !form.sku || !form.category_id || !form.price || !form.cost_price) {
            toast.error('Please fill all required fields');
            return;
        }

        setSaving(true);
        try {
            const data = {
                ...form,
                price: parseFloat(form.price),
                cost_price: parseFloat(form.cost_price),
                stock_quantity: parseInt(form.stock_quantity) || 0,
                low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
                reorder_quantity: parseInt(form.reorder_quantity) || 20,
                reorder_supplier_id: form.reorder_supplier_id || null
            };

            if (existingProduct) {
                // Update existing and add stock
                await productsAPI.update(existingProduct.id, {
                    ...data,
                    stock_quantity: existingProduct.stock_quantity + parseInt(form.stock_quantity)
                });
                toast.success(`Added ${form.stock_quantity} to ${form.name}`);
            } else {
                // Create new product
                await productsAPI.create(data);
                toast.success('Product created!');
            }

            setSheetOpen(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="scan-product-page">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-card border-b border-border">
                <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => navigate(-1)}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="font-bold text-lg">Scan Product</h1>
                            <p className="text-xs text-muted-foreground">Add to inventory</p>
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                        <span className="text-primary-foreground font-bold">H</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="p-4 max-w-lg mx-auto space-y-4">
                {/* Scan Options */}
                <div className="grid grid-cols-2 gap-4">
                    <Card 
                        className="cursor-pointer hover:border-primary transition-colors"
                        onClick={() => setScannerOpen(true)}
                        data-testid="open-scanner-btn"
                    >
                        <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 mx-auto mb-3 bg-primary/10 rounded-full flex items-center justify-center">
                                <Scan className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="font-semibold">Scan Barcode</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                Use camera to scan
                            </p>
                        </CardContent>
                    </Card>

                    <Card 
                        className="cursor-pointer hover:border-primary transition-colors"
                        onClick={() => setManualEntry(!manualEntry)}
                        data-testid="manual-entry-btn"
                    >
                        <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 mx-auto mb-3 bg-secondary rounded-full flex items-center justify-center">
                                <Keyboard className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="font-semibold">Manual Entry</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                Type SKU/barcode
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Manual Barcode Entry */}
                {manualEntry && (
                    <Card className="animate-fadeIn">
                        <CardContent className="p-4">
                            <Label htmlFor="manual-barcode">Enter Barcode/SKU</Label>
                            <div className="flex gap-2 mt-2">
                                <Input
                                    id="manual-barcode"
                                    ref={barcodeInputRef}
                                    value={form.barcode}
                                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                                    onKeyDown={handleManualBarcode}
                                    placeholder="Scan or type barcode..."
                                    autoFocus
                                    data-testid="manual-barcode-input"
                                />
                                <Button 
                                    onClick={() => form.barcode && handleScan(form.barcode)}
                                    disabled={!form.barcode}
                                    data-testid="lookup-btn"
                                >
                                    <Check className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Quick Add Button */}
                <Button 
                    className="w-full h-14 text-lg"
                    onClick={() => {
                        resetForm();
                        setSheetOpen(true);
                    }}
                    data-testid="quick-add-btn"
                >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Product Manually
                </Button>

                {/* Recent Scans */}
                {recentScans.length > 0 && (
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Recent Scans</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ScrollArea className="max-h-48">
                                {recentScans.map((scan, idx) => (
                                    <div 
                                        key={idx}
                                        className="flex items-center justify-between p-3 border-b last:border-0 cursor-pointer hover:bg-secondary/50"
                                        onClick={() => handleScan(scan.barcode)}
                                    >
                                        <div className="flex items-center gap-3">
                                            {scan.found ? (
                                                <Check className="h-4 w-4 text-emerald-600" />
                                            ) : (
                                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                            )}
                                            <div>
                                                <p className="font-mono text-sm">{scan.barcode}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {scan.name || 'New product'}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge variant={scan.found ? 'default' : 'secondary'}>
                                            {scan.found ? 'Found' : 'New'}
                                        </Badge>
                                    </div>
                                ))}
                            </ScrollArea>
                        </CardContent>
                    </Card>
                )}

                {/* Tips */}
                <Card className="bg-muted/50">
                    <CardContent className="p-4">
                        <h4 className="font-semibold text-sm mb-2">Tips</h4>
                        <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• Hold device steady when scanning</li>
                            <li>• Ensure good lighting for best results</li>
                            <li>• Supports UPC, EAN, Code 128, QR codes</li>
                            <li>• Use manual entry for damaged barcodes</li>
                        </ul>
                    </CardContent>
                </Card>
            </main>

            {/* Barcode Scanner Modal */}
            <BarcodeScanner 
                isOpen={scannerOpen}
                onScan={handleScan}
                onClose={() => setScannerOpen(false)}
            />

            {/* Image Capture Modal */}
            <ImageCapture
                isOpen={cameraOpen}
                onCapture={handleImageCapture}
                onClose={() => setCameraOpen(false)}
            />

            {/* Product Form Sheet */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2">
                            {existingProduct ? (
                                <>
                                    <Edit className="h-5 w-5" />
                                    Update Inventory
                                </>
                            ) : (
                                <>
                                    <Package className="h-5 w-5" />
                                    New Product
                                </>
                            )}
                        </SheetTitle>
                        <SheetDescription>
                            {existingProduct 
                                ? `Adding stock to: ${existingProduct.name}` 
                                : 'Fill in the product details'}
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-6 space-y-4">
                        {/* Product Image */}
                        <div className="flex items-center gap-4">
                            <div 
                                className="w-24 h-24 rounded-lg bg-secondary flex items-center justify-center cursor-pointer overflow-hidden"
                                onClick={() => setCameraOpen(true)}
                            >
                                {form.image_url ? (
                                    <img 
                                        src={form.image_url} 
                                        alt="Product" 
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <Camera className="h-8 w-8 text-muted-foreground" />
                                )}
                            </div>
                            <div className="flex-1">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setCameraOpen(true)}
                                >
                                    <Camera className="h-4 w-4 mr-2" />
                                    Take Photo
                                </Button>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Tap image to capture
                                </p>
                            </div>
                        </div>

                        {/* Barcode/SKU */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="barcode">Barcode</Label>
                                <Input
                                    id="barcode"
                                    value={form.barcode}
                                    onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                                    className="font-mono"
                                    data-testid="form-barcode"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sku">SKU *</Label>
                                <Input
                                    id="sku"
                                    value={form.sku}
                                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                                    className="font-mono"
                                    disabled={!!existingProduct}
                                    data-testid="form-sku"
                                />
                            </div>
                        </div>

                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="name">Product Name *</Label>
                            <Input
                                id="name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                disabled={!!existingProduct}
                                data-testid="form-name"
                            />
                        </div>

                        {/* Category */}
                        <div className="space-y-2">
                            <Label>Category *</Label>
                            <Select 
                                value={form.category_id} 
                                onValueChange={(v) => setForm({ ...form, category_id: v })}
                                disabled={!!existingProduct}
                            >
                                <SelectTrigger data-testid="form-category">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {categories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Pricing */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="price">Sell Price *</Label>
                                <Input
                                    id="price"
                                    type="number"
                                    step="0.01"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    disabled={!!existingProduct}
                                    data-testid="form-price"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cost">Cost Price *</Label>
                                <Input
                                    id="cost"
                                    type="number"
                                    step="0.01"
                                    value={form.cost_price}
                                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                                    disabled={!!existingProduct}
                                    data-testid="form-cost"
                                />
                            </div>
                        </div>

                        {/* Stock Quantity - Always editable */}
                        <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                            <Label htmlFor="quantity" className="text-emerald-700 dark:text-emerald-400">
                                {existingProduct ? 'Quantity to Add' : 'Initial Stock'}
                            </Label>
                            <Input
                                id="quantity"
                                type="number"
                                min="1"
                                value={form.stock_quantity}
                                onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })}
                                className="mt-2 text-lg font-bold text-center"
                                data-testid="form-quantity"
                            />
                            {existingProduct && (
                                <p className="text-xs text-muted-foreground mt-2 text-center">
                                    Current stock: {existingProduct.stock_quantity} → 
                                    New total: {existingProduct.stock_quantity + parseInt(form.stock_quantity || 0)}
                                </p>
                            )}
                        </div>

                        {/* Auto Reorder - Only for new products */}
                        {!existingProduct && (
                            <div className="space-y-4 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label>Auto-Reorder</Label>
                                        <p className="text-xs text-muted-foreground">
                                            Auto-order when stock is low
                                        </p>
                                    </div>
                                    <Switch
                                        checked={form.auto_reorder}
                                        onCheckedChange={(checked) => setForm({ ...form, auto_reorder: checked })}
                                    />
                                </div>

                                {form.auto_reorder && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Supplier</Label>
                                            <Select 
                                                value={form.reorder_supplier_id} 
                                                onValueChange={(v) => setForm({ ...form, reorder_supplier_id: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {suppliers.map((sup) => (
                                                        <SelectItem key={sup.id} value={sup.id}>
                                                            {sup.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Reorder Qty</Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={form.reorder_quantity}
                                                onChange={(e) => setForm({ ...form, reorder_quantity: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Save Button */}
                        <Button 
                            className="w-full h-12 text-lg"
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="save-product-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            ) : (
                                <Check className="h-5 w-5 mr-2" />
                            )}
                            {existingProduct ? 'Update Inventory' : 'Add Product'}
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
